import { AnalyticsUpIcon } from "@hugeicons-pro/core-duotone-rounded";
import { useLingui } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatISO, isValid, parseISO } from "date-fns";
import { useState } from "react";
import { DownloadsOverview } from "~/components/charts/downloads-overview";
import { TopCountries } from "~/components/charts/top-countries";
import { TopDevices } from "~/components/charts/top-devices";
import { TopDirectories } from "~/components/charts/top-directories";
import { TopEpisodes } from "~/components/charts/top-episodes";
import { TopPlayers } from "~/components/charts/top-players";
import { ChartFilters } from "~/components/charts/ui/chart-filters";
import { AppContainer } from "~/components/layouts/app/app-container";
import { PageHead } from "~/components/ui/page-head";
import {
	buildQuickFiltersData,
	QUICK_FILTERS,
	type QuickFilter,
	useChartFilters,
} from "~/hooks/user-chart-filters";
import { exportCsv } from "~/lib/export";
import { useTRPC } from "~/trpc/client";

type StatisticsSearch = {
	q?: QuickFilter;
	from?: string;
	to?: string;
};

export const Route = createFileRoute("/_app/podcasts/$channelUid/statistics")({
	component: Page,
	validateSearch: (search: Record<string, unknown>): StatisticsSearch => {
		const q = typeof search.q === "string" && QUICK_FILTERS.includes(search.q) ? search.q : "7d";
		const quickFilters = buildQuickFiltersData();
		const quickFilter = !["all", "custom"].includes(q)
			? quickFilters.find((f) => f.key === q)
			: undefined;

		return {
			q,
			from:
				typeof search.from === "string" && isValid(parseISO(search.from))
					? search.from
					: quickFilter?.value?.from
						? formatISO(quickFilter.value.from, { representation: "date" })
						: undefined,
			to:
				typeof search.to === "string" && isValid(parseISO(search.to))
					? search.to
					: quickFilter?.value?.to
						? formatISO(quickFilter.value.to, { representation: "date" })
						: undefined,
		};
	},
});

function Page() {
	const search = Route.useSearch();
	const { channelUid } = Route.useParams();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [isExporting, setIsExporting] = useState(false);

	const { t } = useLingui();

	const { data: channel } = useQuery(trpc.channel.find.queryOptions({ channelUid }));

	const chartFilters = useChartFilters({
		allStartDate: channel ? new Date(channel.createdDate) : new Date(),
		initialFilter: search.q,
		initialDate:
			search.from && search.to
				? { from: new Date(search.from), to: new Date(search.to) }
				: undefined,
	});

	const filtersWithChannelUid = {
		...chartFilters.filters,
		channelUid,
	};

	async function dailyDownloads() {
		const data = await queryClient.fetchQuery(
			trpc.statistics.reportDailyDownloads.queryOptions({
				channelUids: [channelUid],
				from: chartFilters.filters.from,
				to: chartFilters.filters.to,
			}),
		);

		if (!data) {
			throw Error("This report doesn't have any data to export.");
		}

		const csvContent = data.map((dataPoint) => ({
			date: dataPoint.date,
			episodeUid: dataPoint.episode?.uid || "",
			episodeTitle: dataPoint.episode?.title || "",
			channelUid: dataPoint.episode?.channelUid || "",
			channelTitle: dataPoint.episode?.channelTitle || "",
			publishDate: dataPoint.episode?.publishDate || "",
			downloads: dataPoint.downloads,
		}));

		let title = `report_daily_downloads_${channelUid}`;
		title += `_${chartFilters.filters.from}_${chartFilters.filters.to}`;
		title += `.csv`;

		return {
			title,
			csvContent,
		};
	}
	async function totalDownloads() {
		const data = await queryClient.fetchQuery(
			trpc.statistics.reportTotalDownloads.queryOptions({
				channelUids: [channelUid],
			}),
		);

		if (!data) {
			throw Error("This report doesn't have any data to export.");
		}

		const csvContent = data.map((dataPoint) => ({
			episodeUid: dataPoint.episode?.uid || "",
			episodeTitle: dataPoint.episode?.title || "",
			channelUid: dataPoint.episode?.channelUid || "",
			channelTitle: dataPoint.episode?.channelTitle || "",
			publishDate: dataPoint.episode?.publishDate || "",
			downloads_1_day: dataPoint.downloads["1D"],
			downloads_7_day: dataPoint.downloads["7D"],
			downloads_30_day: dataPoint.downloads["30D"],
			downloads_90_day: dataPoint.downloads["90D"],
			downloads_total: dataPoint.downloads.total,
		}));

		let title = `report_total_downloads_${channelUid}`;
		title += `.csv`;

		return {
			title,
			csvContent,
		};
	}

	const handleExport = async (report: string) => {
		setIsExporting(true);

		try {
			let title = "";
			let csvContent = [];

			switch (report) {
				case "dailyDownloads": {
					const report = await dailyDownloads();

					title = report.title;
					csvContent = report.csvContent;
					break;
				}
				case "totalDownloads": {
					const report = await totalDownloads();

					title = report.title;
					csvContent = report.csvContent;
					break;
				}
				default:
					throw Error("Unknown report type");
			}

			try {
				exportCsv(title, csvContent);
			} catch (error) {
				alert(
					t`An error occurred while exporting the CSV: ${error instanceof Error ? error.message : String(error)}`,
				);
			}
		} catch (e) {
			console.error(e);
			alert(t`Failed to export data. Please try again later.`);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<>
			<PageHead
				icon={AnalyticsUpIcon}
				title={t`Podcast Statistics`}
				subtitle={t`Track your podcast growth with insights into your most popular episodes, and audience trends.`}
			/>
			<AppContainer className="my-12">
				<ChartFilters hook={chartFilters} handleExport={handleExport} isExporting={isExporting} />
				<div className="grid grid-cols-12 gap-6">
					<DownloadsOverview
						className="col-span-full lg:col-span-6"
						channel={channel}
						filters={filtersWithChannelUid}
					/>
					<TopEpisodes
						className="col-span-full lg:col-span-6"
						channel={channel}
						filters={filtersWithChannelUid}
					/>
					<TopCountries
						className="col-span-full"
						channel={channel}
						filters={filtersWithChannelUid}
					/>
					<TopDevices
						className="col-span-full md:col-span-6"
						channel={channel}
						filters={filtersWithChannelUid}
					/>
					<TopPlayers
						className="col-span-full md:col-span-6"
						channel={channel}
						filters={filtersWithChannelUid}
					/>
					<TopDirectories
						className="col-span-full md:col-span-6"
						channel={channel}
						filters={filtersWithChannelUid}
					/>
				</div>
			</AppContainer>
		</>
	);
}
